import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Descriptions, Form, Result, Row, Space, Table } from 'antd';

import { SubscriptionWithChildren } from '@app/domain-subscription-interfaces';
import { Auth, FeatureFlags, Rules } from '@app/web-components';

import { useBundleViewPanel } from '../../bundles';
import { BundleCard } from '../../bundles/atoms';
import { EmployeeTypeRenderer } from '../../common';
import { WorkerCard } from '../../users';
import { useSubscriptionSelectAccess } from '../atoms';
import { useEditSubscription } from '../data';
import { EvidenceModal } from '../modals';

import { useJustificationFormItem, useRetryAccessesTable, useSelectAccessesFormItem } from './inputs';

type FormData = {
  bundleAccessIds: number[];
  justification?: string;
};

export type EditFormProps = {
  record: SubscriptionWithChildren;
  refetch: () => void;
};

export const EditFormOld = ({ record }: EditFormProps) => {
  const navigate = useNavigate();
  const { user } = Auth.useUserAuth();
  const [form] = Form.useForm();
  const edit = useEditSubscription({});
  const { getPanelSpaceProps } = useBundleViewPanel(record.bundle.id);
  const { getProps: getJustificationFormItemProps } = useJustificationFormItem(record.bundle);

  const { getSelectAccessTableProps: props, selectAccessTourStepProps } = useSubscriptionSelectAccess(
    record.bundleId,
    record.user.enterpriseId,
    form,
  );

  const { getProps: getSelectAccessesFormItemProps } = useSelectAccessesFormItem({
    props,
    form,
    selectAccessTourStepProps,
  });

  const initialValues: FormData = {
    bundleAccessIds: record.accesses.filter((x) => x.subscribed).map((access) => access.bundleAccessId),
    justification: record.justification,
  };

  const handleResetForm = () => {
    form.resetFields();
    edit.reset();
  };

  const handleSubmit = (data: FormData) => {
    edit.mutate({ id: record.id, bundleAccessIds: data.bundleAccessIds, justification: data.justification });
  };

  if (edit.isSuccess && edit.data.data.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully modified a subscription!"
        subTitle={`Bundle name: ${edit.data.data.mutated?.bundle.name} for user: ${edit.data.data.mutated?.user.name}`}
        extra={[
          <Button type="primary" key="go to subscriptions" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
        ]}
      />
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ width: '100%', minWidth: 1000 }}
      disabled={edit.isPending || edit.isSuccess}
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      {edit.error ? (
        <Alert
          message="Error"
          description={`Something went wrong. Error: ${edit.error}`}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        />
      ) : undefined}
      {edit.isSuccess && !edit.data.data.isSuccess ? (
        <Alert
          message="Sorry, subscribing to this bundle is not possible."
          description={<Rules.RuleMessagesPanel messages={edit.data.data.messages} />}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        ></Alert>
      ) : undefined}
      <Form.Item label="Worker">
        <Descriptions
          bordered
          column={2}
          items={[
            { key: 'name', label: 'Name', children: record.user.name },
            { key: 'wwid', label: 'WWID', children: record.user.enterpriseId },
            {
              key: 'email',
              label: 'Email',
              children: <a href={`mailto:${record.user.email}`}>{record.user.email}</a>,
            },
            { key: 'idsid', label: 'IDSID', children: record.user.idsid },
            {
              key: 'employee-type',
              label: 'Employee Type',
              children: <EmployeeTypeRenderer employeeType={record.user.employeeType} />,
            },
            ...(user?.isSystemAdmin
              ? [
                  {
                    key: 'evidence',
                    label: 'SYSTEM ADMIN ONLY',
                    children: <EvidenceModal enterpriseId={record.user.enterpriseId} />,
                  },
                ]
              : []),
          ]}
          size="small"
        />
      </Form.Item>
      <Form.Item label="Bundle">
        <Space {...getPanelSpaceProps()} />
      </Form.Item>
      <Form.Item {...getSelectAccessesFormItemProps()} />
      <Form.Item {...getJustificationFormItemProps()} />
      <Space style={{ marginTop: 20 }}>
        <Button type="primary" htmlType="submit" loading={edit.isPending}>
          {record.status?.isActive ? 'Save Changes' : 'Repush'}
        </Button>
        <Button onClick={handleResetForm}>Reset</Button>
        <Button onClick={() => navigate('../')}>Cancel</Button>
      </Space>
    </Form>
  );
};

export const EditFormNew = ({ record, refetch }: EditFormProps) => {
  const [editable, setEditable] = useState(false);

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const edit = useEditSubscription({});
  const { getProps: getJustificationFormItemProps } = useJustificationFormItem(record.bundle);
  const { getSelectAccessTableProps: props, selectAccessTourStepProps } = useSubscriptionSelectAccess(
    record.bundle.id,
    record.user.enterpriseId,
    form,
  );

  const { getProps: getSelectAccessesFormItemProps } = useSelectAccessesFormItem({
    props,
    form,
    selectAccessTourStepProps,
  });

  const { getProps: getRetryAccessTableProps } = useRetryAccessesTable(record, refetch);

  const initialValues: FormData = {
    bundleAccessIds: record.accesses.filter((x) => x.subscribed).map((access) => access.bundleAccessId),
    justification: record.justification,
  };

  const handleResetForm = () => {
    form.resetFields();
    edit.reset();
  };

  const handleSubmit = (data: FormData) => {
    edit.mutate({ id: record.id, bundleAccessIds: data.bundleAccessIds, justification: data.justification });
  };

  if (edit.isSuccess && edit.data.data.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully modified a subscription!"
        subTitle={`Bundle name: ${edit.data.data.mutated?.bundle.name} for user: ${edit.data.data.mutated?.user.name}`}
        extra={[
          <Button type="primary" key="go to subscriptions" onClick={() => navigate('../')}>
            Go To Subscriptions
          </Button>,
        ]}
      />
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ width: '100%', minWidth: 1000 }}
      disabled={edit.isPending || edit.isSuccess}
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      {edit.error ? (
        <Alert
          message="Error"
          description={`Something went wrong. Error: ${edit.error}`}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        />
      ) : undefined}
      {edit.isSuccess && !edit.data.data.isSuccess ? (
        <Alert
          message="Sorry, subscribing to this bundle is not possible."
          description={<Rules.RuleMessagesPanel messages={edit.data.data.messages} />}
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
        ></Alert>
      ) : undefined}
      <Button
        icon={<EditOutlined />}
        type="primary"
        htmlType="button"
        style={{ marginBottom: '1em' }}
        onClick={() => setEditable(true)}
      >
        Edit
      </Button>
      <Row gutter={16} style={{ marginBottom: '1em' }}>
        <Col span={12}>
          <WorkerCard worker={record.user} />
        </Col>
        <Col span={12}>
          <BundleCard bundleId={record.bundle.id} />
        </Col>
      </Row>
      {editable ? (
        <>
          <Form.Item {...getSelectAccessesFormItemProps()} />
          <Form.Item {...getJustificationFormItemProps()} />
          <Space style={{ marginTop: 20 }}>
            <Button type="primary" htmlType="submit" loading={edit.isPending}>
              {record.status?.isActive ? 'Save Changes' : 'Repush'}
            </Button>
            <Button onClick={handleResetForm}>Reset</Button>
            <Button onClick={() => setEditable(false)}>Cancel</Button>
          </Space>
        </>
      ) : (
        <>
          <Form.Item label="Accesses">
            <Table {...getRetryAccessTableProps()} />
          </Form.Item>
          <Descriptions bordered items={[{ label: 'Justification ', children: record.justification }]} />
          <Space style={{ marginTop: 20 }}>
            <Button style={{ display: 'none' }} />
          </Space>
        </>
      )}
    </Form>
  );
};

export const EditForm = ({ record, refetch }: EditFormProps) => {
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();

  return isFeatureEnabled('subscription_edit_retry') ? (
    <EditFormNew record={record} refetch={refetch} />
  ) : (
    <EditFormOld record={record} refetch={refetch} />
  );
};
